/**
 * Trophy Room Page (Owner)
 * Story 3.6.d: Owner Trophy Room Management Frontend
 *
 * Pure view component following strict MVVM pattern.
 * All business logic is in useTrophyRoomViewModel.
 *
 * Features:
 * - Display all Trophy Room items (public + private)
 * - Filter by product type (all, patches, clothing)
 * - Toggle item visibility (public/private)
 * - Unpair items with confirmation
 * - Report items as lost/stolen with modal
 * - Loading states for all operations
 * - Error display with auto-dismiss
 * - Empty state when no items
 */

import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTrophyRoomViewModel } from '@/viewModels/useTrophyRoomViewModel';
import { PrivateCollectionItem } from '@/ui/components/PrivateCollectionItem';
import { PrivateCollectionItemSkeleton } from '@/ui/components/ui/PrivateCollectionItemSkeleton';
import { CollectionSwitch, type CollectionFilter } from '@/ui/components/collection-switch';
import { MultiChoiceModal, type Choice } from '@/ui/components/ui/multi-choice-modal';
import { ErrorState, PageTitle } from '@/ui/components';
import { TrophyRoomEmptyState } from '@/ui/components/TrophyRoomEmptyState';
import { AppLayout } from '@/ui/layouts';
import type { TrophyRoomItem, ReportType } from '@/domain/ports/ITrophyRoomGateway';
import { parseNfcIdentifier } from '@/utils/nfcUrlParser';
import { toast } from 'sonner';

// Unpair report type choices for the modal (AC 2)
const unpairChoices: Choice[] = [
  { id: 'unused', label: 'No longer using' },
  { id: 'lost', label: 'Lost' },
  { id: 'stolen', label: 'Stolen' },
  { id: 'sold', label: 'Sold' },
];

/**
 * Trophy Room Page Component
 *
 * Displays the authenticated owner's Trophy Room with all items.
 * Uses useTrophyRoomViewModel for all state and business logic.
 */
export const TrophyRoomPage: React.FC = () => {
  const { t } = useTranslation();
  const { state, actions, computed, onMount } = useTrophyRoomViewModel();
  const [unpairModalOpen, setUnpairModalOpen] = useState(false);
  const [selectedItemForUnpair, setSelectedItemForUnpair] = useState<TrophyRoomItem | null>(null);

  // Mount effect - fetch items on mount and cleanup on unmount
  useEffect(() => {
    return onMount();
  }, [onMount]);

  // Convert ViewModel filter mode to CollectionSwitch filter type
  const collectionFilter: CollectionFilter =
    state.filterMode === 'patches' ? 'patch' :
    state.filterMode === 'clothing' ? 'clothes' :
    null;

  // Handle filter change from CollectionSwitch
  const handleFilterChange = (filter: CollectionFilter) => {
    const filterMode = filter === 'patch' ? 'patches' : filter === 'clothes' ? 'clothing' : 'all';
    actions.setFilterMode(filterMode);
  };

  // Handle unpair click - show modal
  const handleUnpairClick = (item: TrophyRoomItem) => {
    setSelectedItemForUnpair(item);
    setUnpairModalOpen(true);
  };

  // Handle visibility toggle
  const handleVisibilityChange = async (item: TrophyRoomItem, visible: boolean) => {
    const visibility = visible ? 'public' : 'private';
    console.log('[TrophyRoomPage] Visibility toggle clicked:', {
      itemUrl: item.url,
      productName: item.product?.name,
      newVisibility: visibility,
      visible,
    });

    try {
      await actions.updateItemVisibility(item.url, visibility);
      console.log('[TrophyRoomPage] Visibility update completed');

      // Show success toast
      const message = visibility === 'public'
        ? 'Item is now visible in your public Trophy Room'
        : 'Item is now private';
      console.log('[TrophyRoomPage] Calling toast.success with:', message);
      toast.success(message);
      console.log('[TrophyRoomPage] toast.success called');
    } catch (error) {
      console.error('[TrophyRoomPage] Visibility update failed:', error);
      toast.error('Failed to update item visibility');
    }
  };

  // Handle unpair confirmation (AC 4: Dynamic toast messages)
  const handleConfirmUnpair = async (reportType: ReportType) => {
    if (selectedItemForUnpair) {
      try {
        await actions.unpairItem(selectedItemForUnpair.url, reportType);

        // Dynamic toast message based on reportType (AC 4)
        const toastMessages: Record<ReportType, string> = {
          'lost': 'Item reported as lost and unpaired successfully',
          'stolen': 'Item reported as stolen and unpaired successfully',
          'sold': 'Item reported as sold and unpaired successfully',
          'unused': 'Item unpaired successfully',
        };

        toast.success(toastMessages[reportType]);
      } catch (error) {
        // Error toast already handled in ViewModel
      } finally {
        setUnpairModalOpen(false);
        setSelectedItemForUnpair(null);
      }
    }
  };

  // Handle cancel unpair
  const handleCancelUnpair = () => {
    setUnpairModalOpen(false);
    setSelectedItemForUnpair(null);
  };

  // Render full-page error state (initial fetch failures with NO items loaded)
  if (state.error && !state.loading && computed.totalItemCount === 0) {
    return (
      <AppLayout>
        <PageTitle>{t("trophyRoom.title", "MY COLLECTION")}</PageTitle>
        <ErrorState
          message={t(
            "trophyRoom.errorLoading",
            "An error occurred while loading your collection. Please retry."
          )}
          onRetry={actions.refresh}
        />
      </AppLayout>
    );
  }

  // Render loading state
  if (state.loading && computed.totalItemCount === 0) {
    return (
      <AppLayout>
        <div
          role="status"
          aria-live="polite"
          aria-label="Loading your Trophy Room"
        >
          <PageTitle>{t("trophyRoom.title", "MY COLLECTION")}</PageTitle>

          {/* Page Header Skeleton */}
          <div className="animate-pulse flex flex-col gap-[1.5rem] mt-8">
            {/* CollectionSwitch Skeleton */}
            <div className="flex gap-[0.5rem]">
              <div className="h-10 bg-gray-100 rounded w-24" />
              <div className="h-10 bg-gray-100 rounded w-24" />
              <div className="h-10 bg-gray-100 rounded w-24" />
            </div>
          </div>

          {/* Collection Items Skeletons (3 items) */}
          <div className="flex flex-col gap-[1.5rem] mt-8">
            <PrivateCollectionItemSkeleton />
            <PrivateCollectionItemSkeleton />
            <PrivateCollectionItemSkeleton />
          </div>
        </div>
      </AppLayout>
    );
  }

  // Render empty state
  if (computed.isEmptyState) {
    return (
      <AppLayout>
        <PageTitle>{t("trophyRoom.title", "MY COLLECTION")}</PageTitle>

        {/* CollectionSwitch - still visible when empty */}
        <div className="mt-8">
          <CollectionSwitch
            value={collectionFilter}
            onChange={handleFilterChange}
          />
        </div>

        {/* Empty state below filter */}
        <TrophyRoomEmptyState variant="owner" />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {/* Page Title */}
      <PageTitle>{t("trophyRoom.title", "MY COLLECTION")}</PageTitle>

      {/* Filter */}
      {computed.hasItems && (
        <div className="mt-8">
          <CollectionSwitch
            value={collectionFilter}
            onChange={handleFilterChange}
          />
        </div>
      )}

      {/* Error Display - Inline banner for partial errors (when items ARE loaded) */}
      {state.error && computed.totalItemCount > 0 && (
        <div className="flex items-center justify-between p-4 bg-red-100 border border-red-200 rounded-md mt-4">
          <p className="font-montserrat text-sm text-red-900">{state.error}</p>
          <button
            onClick={actions.dismissError}
            className="font-montserrat text-sm text-red-700 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Items List */}
      <div className="flex flex-col gap-[1.5rem] mt-8">
        {computed.filteredItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="font-montserrat text-gray-500">
              No items match the current filter.
            </p>
          </div>
        ) : (
          computed.filteredItems.map((item) => (
            /* Report button hidden per Story 3.6.b.1 - merged into unpair flow */
            <PrivateCollectionItem
              key={item.url}
              productImage={item.product?.imageUrl || ''}
              brandName={item.product?.brandName || 'ANYW3AR'}
              productName={item.product?.name || item.product?.title || ''}
              nfcId={parseNfcIdentifier(item.url)}
              productNumber={{
                current: item.itemNumber ?? 0,
                total: item.product?.editionSize ?? 0,
              }}
              productSize={item.product?.size || 'N/A'}
              isVisible={item.visibility === 'public'}
              onVisibilityChange={(visible) => handleVisibilityChange(item, visible)}
              onUnpair={() => handleUnpairClick(item)}
              unPairBtnHidden={state.unpairing === item.url}
              partnerLink={
                item.partnerLink
                  ? {
                      label: "PARTNER'S LINK",
                      onClick: () => window.open(item.partnerLink, '_blank', 'noopener,noreferrer')
                    }
                  : undefined
              }
            />
          ))
        )}
      </div>

      {/* Unpair Modal with Report Type Selection (AC 2, 3, 4) */}
      {unpairModalOpen && selectedItemForUnpair && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 p-4">
          <div className="max-w-md w-full">
            <UnpairModal
              itemName={selectedItemForUnpair.product?.name || selectedItemForUnpair.product?.title || ''}
              onCancel={handleCancelUnpair}
              onConfirm={handleConfirmUnpair}
            />
          </div>
        </div>
      )}
    </AppLayout>
  );
};

/**
 * Unpair Modal Component (AC 2, 3, 4)
 *
 * Wraps MultiChoiceModal to provide:
 * - Single-choice mode with 4 radio button options (AC 2)
 * - Dynamic button labels ('UNPAIR' vs 'REPORT') (AC 3)
 * - Dynamic toast messages based on selection (AC 4 - handled by parent)
 */
interface UnpairModalProps {
  itemName: string;
  onCancel: () => void;
  onConfirm: (reportType: ReportType) => Promise<void>;
}

const UnpairModal: React.FC<UnpairModalProps> = ({ itemName, onCancel, onConfirm }) => {
  const [selectedReportType, setSelectedReportType] = useState<ReportType>('unused');

  const handleConfirm = async (selectedIds: string[]) => {
    if (selectedIds.length > 0) {
      const reportType = selectedIds[0] as ReportType;
      await onConfirm(reportType);
    }
  };

  const handleSelectionChange = (selectedIds: string[]) => {
    if (selectedIds.length > 0) {
      setSelectedReportType(selectedIds[0] as ReportType);
    }
  };

  return (
    <MultiChoiceModal
      message={`Why are you unpairing "${itemName || 'this item'}"?`}
      choices={unpairChoices}
      mode="single"
      defaultSelectedId="unused"
      cancelText="CANCEL"
      confirmText={selectedReportType === 'unused' ? 'UNPAIR' : 'REPORT'} // AC 3: Dynamic button label
      onCancel={onCancel}
      onConfirm={handleConfirm}
      onChange={handleSelectionChange} // Track selection for dynamic button label
    />
  );
};

TrophyRoomPage.displayName = 'TrophyRoomPage';
